import { AxiosRequestConfig, AxiosResponse } from "axios";
import HttpClient from "./HttpClient";
import { AuthService } from "../../Modules/Auth/services/AuthService";
import { AppConstant } from "./AppConstant";

type RequestMethod = "POST" | "PUT" | "PATCH" | "GET" | "DELETE";
type ContentType = "json" | "form-data" | "urlencoded";

export interface HttpRequestOptions<T> {
  url: string;
  method: RequestMethod;
  data?: T;
  config?: AxiosRequestConfig;
  contentType?: ContentType;
}

export class HttpService {
  static async request<TRequest = any, TResponse = any>({
    url,
    method,
    data,
    config = {},
    contentType = "json",
  }: HttpRequestOptions<TRequest>): Promise<TResponse> {
    try {
      let requestData: unknown = data;
      const headers = config.headers ?? {};
      const token = AuthService.getAccessToken();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      switch (contentType) {
        case "form-data":
          const form = new FormData();
          Object.entries(data || {}).forEach(([key, value]) => {
            form.append(key, value as any);
          });
          requestData = form;
          headers["Content-Type"] = "multipart/form-data";
          break;

        case "urlencoded":
          const params = new URLSearchParams();
          Object.entries(data || {}).forEach(([key, value]) => {
            params.append(key, value as any);
          });
          requestData = params;
          headers["Content-Type"] = "application/x-www-form-urlencoded";
          break;

        case "json":
        default:
          headers["Content-Type"] = "application/json";
          break;
      }

      const finalConfig: AxiosRequestConfig = {
        ...config,
        headers,
      };

      let response: AxiosResponse<TResponse>;

      switch (method) {
        case "GET":
          response = await HttpClient.get(url, finalConfig);
          break;
        case "POST":
          response = await HttpClient.post(url, requestData, finalConfig);
          break;
        case "PUT":
          response = await HttpClient.put(url, requestData, finalConfig);
          break;
        case "PATCH":
          response = await HttpClient.patch(url, requestData, finalConfig);
          break;
        case "DELETE":
          response = await HttpClient.delete(url, finalConfig);
          break;
        default:
          throw new Error(`Unsupported request method: ${method}`);
      }

      return response.data;
    } catch (error: any) {
      const response = error.response;
      if (response?.status === 401) {
        AuthService.logout();
      }
      if (response?.data?.message === AppConstant.JwtExpired) {
        AuthService.logout();
      }
      throw response?.data || new Error("Generic Request Error");
    }
  }
}
